import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */


/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  constructor() {
    super();

    // Bind methods so "this" is correct
    this.onQuantityChange = this.onQuantityChange.bind(this);
    this.onLineItemRemove = this.onLineItemRemove.bind(this);
    this.updateQuantity = this.updateQuantity.bind(this);
    this.handleDiscountUpdate = this.handleDiscountUpdate.bind(this);
    this.handleCartUpdate = this.handleCartUpdate.bind(this);
    this.handleCartError = this.handleCartError.bind(this);
    this.disableCartItems = this.disableCartItems.bind(this);
    this.enableCartItems = this.enableCartItems.bind(this);

    // Debounced handler instead of private field
    this.debouncedOnChange = debounce(this.onQuantityChange, 300);
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail || {};

    if (!line && line !== 0) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });

    const lineItemRow = this.refs.cartItemRows && this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    /** @type {TextComponent | undefined} */
    const textComponent = lineItemRow.querySelector('text-component');
    if (textComponent && typeof textComponent.shimmer === 'function') {
      textComponent.shimmer();
    }
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows && this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...(this.refs.cartItemRows || []).filter(
        (row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key
      ),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) {
        remove();
        return;
      }

      row.style.setProperty('--row-height', row.clientHeight + 'px');
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(
      (config.action || 'update') + ':user-action'
    );

    this.disableCartItems();

    const line = config.line;
    const quantity = config.quantity;
    const cartTotal = this.refs.cartTotal;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);

    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

 const body = JSON.stringify({
  line: line,
  quantity: quantity,

  // SAFARI FIX — must be an array, not a CSV string
  sections: Array.from(sectionsToUpdate),

  sections_url: window.location.pathname,
});

// ========== DEBUG LINES ==========
console.log("DEBUG - sending sections array:", Array.from(sectionsToUpdate));
console.log("DEBUG - sectionId:", this.sectionId);
console.log("DEBUG - request body:", JSON.parse(body));
// =================================

// FETCH CALL
fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }))
  .then((response) => response.text())
  .then((responseText) => {
    let parsedResponseText;

    try {
      parsedResponseText = JSON.parse(responseText);
    } catch (e) {
      console.error('Failed to parse cart change response', e);
      return;
    }

    resetShimmer(this);

    if (parsedResponseText.errors) {
      this.handleCartError(line, parsedResponseText);
      return;
    }

    // DEBUG what Safari received
    console.log("DEBUG - Safari response sections:", parsedResponseText.sections);
    console.log("DEBUG - Looking for sectionId:", this.sectionId);

    const html = parsedResponseText.sections && parsedResponseText.sections[this.sectionId];
    
    if (!html) {
      console.warn("DEBUG - No HTML returned for sectionId on Safari:", this.sectionId);
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }

    const newSectionHTML = new DOMParser().parseFromString(html, 'text/html');

    // Grab the new cart item count from a hidden element
    const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
    const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

    this.dispatchEvent(
      new CartUpdateEvent({}, this.sectionId, {
        itemCount: newCartItemCount,
        source: 'cart-items-component',
        sections: parsedResponseText.sections,
      })
    );

    morphSection(this.sectionId, html);
  })
  .catch((error) => {
    console.error(error);
  })
  .finally(() => {
    this.enableCartItems();
    cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
  });

    fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }))
      .then((response) => response.text())
      .then((responseText) => {
        let parsedResponseText;

        try {
          parsedResponseText = JSON.parse(responseText);
        } catch (e) {
          console.error('Failed to parse cart change response', e);
          return;
        }

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.handleCartError(line, parsedResponseText);
          return;
        }

        const html = parsedResponseText.sections && parsedResponseText.sections[this.sectionId];
        
        if (!html) {
          sectionRenderer.renderSection(this.sectionId, { cache: false });
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(html, 'text/html');

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, html);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate(event) {
    this.handleCartUpdate(event);
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  handleCartError(line, parsedResponseText) {
    const quantitySelector = this.refs.quantitySelectors && this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector && quantitySelector.querySelector('input');

    if (!quantityInput) {
      console.error('Quantity input not found');
      return;
    }

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs['cartItemError-' + line];
    const cartItemErrorContainer = this.refs['cartItemErrorContainer-' + line];

    if (!(cartItemError instanceof HTMLElement)) {
      console.error('Cart item error not found');
      return;
    }
    if (!(cartItemErrorContainer instanceof HTMLElement)) {
      console.error('Cart item error container not found');
      return;
    }

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  }

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  handleCartUpdate(event) {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }

    if (event.target === this) return;

    const sections = event.detail && event.detail.data && event.detail.data.sections;
    const cartItemsHtml = sections && sections[this.sectionId];

    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  }

  /**
   * Disables the cart items.
   */
  disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const sectionId = this.dataset && this.dataset.sectionId;

    if (!sectionId) {
      console.error('Section id missing on cart-items-component', this);
      return '';
    }

    return sectionId;
  }
}

if (window && window.customElements && !window.customElements.get('cart-items-component')) {
  window.customElements.define('cart-items-component', CartItemsComponent);
}
